import { useState, useEffect, useCallback } from 'react';
import { Agent, CreateAgentRequest, UpdateAgentRequest } from '../types/agent';
import { Prompt } from '../types/prompt';
import { API_URLS } from '../utils/constants';

type AgentTab = 'list' | 'create' | 'edit';

interface UseAgentPanelReturn {
  activeTab: AgentTab;
  selectedAgent: Agent | null;
  agents: Agent[];
  prompts: Prompt[];
  loading: boolean;
  error: string | null;
  actions: {
    setActiveTab: (tab: AgentTab) => void;
    selectAgent: (agent: Agent) => void;
    createAgent: (data: CreateAgentRequest) => Promise<void>;
    updateAgent: (id: number, data: UpdateAgentRequest) => Promise<void>;
    deleteAgent: (id: number) => Promise<void>;
    clearError: () => void;
  };
}

export const useAgentPanel = (): UseAgentPanelReturn => {
  const [activeTab, setActiveTab] = useState<AgentTab>('list');
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URLS.admin}/agents`);
      if (!response.ok) throw new Error('Failed to fetch agents');
      const data = await response.json();
      setAgents(data.agents || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading agents');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPrompts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URLS.admin}/prompts`);
      if (!response.ok) throw new Error('Failed to fetch prompts');
      const data = await response.json();
      setPrompts(data.prompts || []);
    } catch (err) {
      console.error('Error loading prompts:', err);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
    fetchPrompts();
  }, [fetchAgents, fetchPrompts]);

  const selectAgent = (agent: Agent) => {
    setSelectedAgent(agent);
    setActiveTab('edit');
  };

  const createAgent = async (data: CreateAgentRequest) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URLS.admin}/agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create agent');
      }
      
      await fetchAgents();
      setActiveTab('list');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error creating agent');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateAgent = async (id: number, data: UpdateAgentRequest) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URLS.admin}/agents/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update agent');
      }
      
      await fetchAgents();
      setActiveTab('list');
      setSelectedAgent(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error updating agent');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteAgent = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres desactivar este agente?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URLS.admin}/agents/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete agent');
      }
      
      await fetchAgents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error deleting agent');
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => setError(null);

  return {
    activeTab,
    selectedAgent,
    agents,
    prompts,
    loading,
    error,
    actions: {
      setActiveTab,
      selectAgent,
      createAgent,
      updateAgent,
      deleteAgent,
      clearError
    }
  };
};
